import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  Bot,
  Search,
  Filter,
  Edit,
  Trash2,
  Eye,
  Settings,
  ChevronDown,
  ChevronRight,
  X,
  AlertTriangle,
  CheckCircle,
  Loader2,
  Sparkles,
  ArrowLeft
} from 'lucide-react';
import { useProjectStore } from '../../store';
import { useContextualNavigation } from '../../hooks/useContextualNavigation';
import { Agent, Model, OutputType, CreateAgentForm, AgentTemplate } from '../../types';
import { AxiosError } from 'axios';
import apiService from '../../services/api';
import AgentFormModal from './AgentFormModal';
import AgentDetailsModal from './AgentDetailsModal';
import ModelSchemaPreview from './ModelSchemaPreview';
import ConfirmationDialog from '../common/ConfirmationDialog';
import CustomSelect from '../common/CustomSelect';
import AgentTable from './AgentTable';
import { AgentTemplateSelector } from './AgentTemplateSelector';
import { useAgents } from '../../hooks/queries/useAgents';
import { useModels } from '../../hooks/queries/useModels';
import {
  useCreateAgent,
  useUpdateAgent,
  useDeleteAgent,
} from '../../hooks/queries/useAgentMutations';
import { queryKeys } from '../../hooks/queries/queryKeys';

interface AgentFilters {
  search: string;
  outputType: OutputType | 'all';
  hasModels: boolean | 'all';
}

interface SortConfig {
  field: 'name' | 'created_at' | 'updated_at';
  direction: 'asc' | 'desc';
}

const AgentsPage: React.FC = () => {
  // AC-1411 / AC-1377 P4.5: AgentsPage now reads agents and models through
  // the canonical TanStack Query hooks (`useAgents`, `useModels`) and routes
  // CRUD through mutation hooks (`useCreateAgent`, `useUpdateAgent`,
  // `useDeleteAgent`). The project store's `agents` / `models` slices are
  // intentionally still wired up during the transition - AC-1412 deletes
  // them once nothing reads from them. Manual `loadAgents()` / `loadModels()`
  // reload calls after mutations are GONE: the mutation hooks invalidate
  // `queryKeys.projectAgents(projectId)` / `queryKeys.projectModels(projectId)`
  // on success, so the list refresh happens automatically.
  const { currentProject } = useProjectStore();
  const projectId = currentProject?.project_id;
  const queryClient = useQueryClient();

  const agentsQuery = useAgents(projectId);
  const modelsQuery = useModels(projectId);

  const agents = useMemo(() => agentsQuery.data ?? [], [agentsQuery.data]);
  const models = useMemo(() => modelsQuery.data ?? [], [modelsQuery.data]);

  // Mutation hooks. Closures captured at projectId; the hooks tolerate an
  // empty-string placeholder when projectId is not yet known, but in
  // practice every mutation call site is gated by `if (!currentProject) return`.
  const createAgent = useCreateAgent(projectId ?? '');
  const updateAgent = useUpdateAgent(projectId ?? '');
  const deleteAgentMutation = useDeleteAgent(projectId ?? '');

  const { getBackToWorkflow } = useContextualNavigation();
  const backToWorkflow = getBackToWorkflow(currentProject?.project_id);
  const [error, setError] = useState<string | null>(null);
  // Local "in-flight" flag for actions that combine async work outside the
  // mutation hooks (e.g. opening the form after refetching models).
  const [actionLoading, setActionLoading] = useState(false);
  const loading =
    actionLoading ||
    agentsQuery.isLoading ||
    modelsQuery.isLoading;
  const [showFormModal, setShowFormModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);
  const [editingAgent, setEditingAgent] = useState<Agent | null>(null);
  const [agentToDelete, setAgentToDelete] = useState<Agent | null>(null);
  
  // Filters and sorting
  const [filters, setFilters] = useState<AgentFilters>({
    search: '',
    outputType: 'all',
    hasModels: 'all'
  });
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: 'created_at',
    direction: 'desc'
  });
  const [showFilters, setShowFilters] = useState(false);
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [showCreateDropdown, setShowCreateDropdown] = useState(false);
  const [showEmptyStateDropdown, setShowEmptyStateDropdown] = useState(false);
  const [templateFormData, setTemplateFormData] = useState<Partial<CreateAgentForm> | null>(null);
  const [agentWorkflows, setAgentWorkflows] = useState<Record<number, { id: number; name: string }[]>>({});
  const [deleteBlockedWorkflows, setDeleteBlockedWorkflows] = useState<{ id: number; name: string }[] | null>(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.create-dropdown-container')) {
        setShowCreateDropdown(false);
        setShowEmptyStateDropdown(false);
      }
    };

    if (showCreateDropdown || showEmptyStateDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showCreateDropdown, showEmptyStateDropdown]);

  // AC-1411: `loadData` is now an invalidation seam. The actual fetch is
  // owned by `useAgents` / `useModels`. Callers (assistant-action listener,
  // explicit "refresh" paths) invalidate the per-project cache and the
  // hooks refetch automatically. Mutation hooks already invalidate on
  // success, so this is only needed for out-of-band events that bypass our
  // mutations (e.g. an assistant action firing on the backend).
  const loadData = useCallback(() => {
    if (!projectId) return;
    queryClient.invalidateQueries({
      queryKey: queryKeys.projectAgents(projectId),
    });
    queryClient.invalidateQueries({
      queryKey: queryKeys.projectModels(projectId),
    });
  }, [projectId, queryClient]);

  // The data-load effect that called loadAgents/loadModels on mount is
  // gone - the hooks fetch automatically when projectId is truthy.

  // Surface query errors to the existing error banner so the page error
  // state matches the prior behavior.
  useEffect(() => {
    if (agentsQuery.isError || modelsQuery.isError) {
      setError('Failed to load agents and models');
    }
  }, [agentsQuery.isError, modelsQuery.isError]);

  // Fetch workflows for each agent
  useEffect(() => {
    const fetchWorkflows = async () => {
      const workflowMap: Record<number, { id: number; name: string }[]> = {};
      await Promise.all(
        agents.map(async (agent) => {
          try {
            const workflows = await apiService.getAgentWorkflows(String(agent.agent_id));
            if (workflows.length > 0) {
              workflowMap[agent.agent_id] = workflows;
            }
          } catch {
            // Silently ignore - workflow info is supplementary
          }
        })
      );
      setAgentWorkflows(workflowMap);
    };
    if (agents.length > 0) {
      fetchWorkflows();
    } else {
      setAgentWorkflows({});
    }
  }, [agents]);

  // Listen for assistant actions and refresh data
  useEffect(() => {
    const handleAssistantAction = (event: CustomEvent) => {
      const { action, projectId: actionOperationId } = event.detail;
      
      // Only refresh if the action affects the current project
      if (currentProject && actionOperationId && String(actionOperationId) === String(currentProject.project_id)) {
        if (action === 'create_agent' || action === 'update_agent' || action === 'delete_agent') {
          loadData();
        }
      }
    };

    // Add event listener
    window.addEventListener('assistant-action', handleAssistantAction as EventListener);
    
    // Cleanup
    return () => {
      window.removeEventListener('assistant-action', handleAssistantAction as EventListener);
    };
  }, [currentProject, loadData]);

  // Filter and sort agents
  const filteredAndSortedAgents = useMemo(() => {
    let filtered = agents.filter(agent => {
      // Search filter
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        const matchesSearch =
          agent.name.toLowerCase().includes(searchLower) ||
          agent.instructions?.toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }

      // Output type filter
      if (filters.outputType !== 'all' && agent.output_type !== filters.outputType) {
        return false;
      }

      // Has models filter
      if (filters.hasModels !== 'all') {
        const hasModel = !!agent.output_model_id;
        if (filters.hasModels !== hasModel) return false;
      }

      return true;
    });

    // Sort
    filtered.sort((a, b) => {
      let aValue: any = a[sortConfig.field];
      let bValue: any = b[sortConfig.field];

      if (sortConfig.field === 'created_at' || sortConfig.field === 'updated_at') {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      } else if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [agents, filters, sortConfig]);

  const handleCreateAgent = async () => {
    if (!currentProject) return;

    setActionLoading(true);
    try {
      // Ensure models are fresh before opening the form. Refetch via the
      // RQ hook so the cache stays the source of truth.
      await modelsQuery.refetch();
      setEditingAgent(null);
      setTemplateFormData(null);
      setShowFormModal(true);
      setShowCreateDropdown(false);
      setShowEmptyStateDropdown(false);
    } catch (err) {
      setError('Failed to load models');
      console.error('Error loading models:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleCreateFromTemplate = () => {
    setShowTemplateSelector(true);
    setShowCreateDropdown(false);
    setShowEmptyStateDropdown(false);
  };

  const handleTemplateSelect = async (template: AgentTemplate) => {
    if (!currentProject) return;

    setActionLoading(true);
    try {
      // Ensure models are fresh before opening the form
      await modelsQuery.refetch();

      // Prepare form data from template
      const formData: Partial<CreateAgentForm> = {
        name: template.name,
        instructions: template.instructions,
        model: template.model,
        model_settings: template.model_settings || {},
        output_type: (template.output_type as OutputType) || 'str',
        tools: template.tool_configurations?.map(tool => ({
          type: tool.type as any,
          name: tool.name,
          description: tool.description,
          ...(tool.mcp_server_id != null && { mcp_server_id: tool.mcp_server_id }),
          ...(tool.mcp_tool_name && { mcp_tool_name: tool.mcp_tool_name }),
        })) || []
      };

      setTemplateFormData(formData);
      setEditingAgent(null);
      setShowTemplateSelector(false);
      setShowFormModal(true);
    } catch (err) {
      setError('Failed to load template');
      console.error('Error loading template:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleEditAgent = async (agent: Agent) => {
    if (!currentProject) return;

    setActionLoading(true);
    try {
      // Ensure models are fresh before opening the form
      await modelsQuery.refetch();
      setEditingAgent(agent);
      setShowFormModal(true);
    } catch (err) {
      setError('Failed to load models');
      console.error('Error loading models:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleViewAgent = (agent: Agent) => {
    setSelectedAgent(agent);
    setShowDetailsModal(true);
  };

  const handleDeleteAgent = (agent: Agent) => {
    setAgentToDelete(agent);
    setShowDeleteDialog(true);
  };

  const confirmDeleteAgent = async () => {
    if (!agentToDelete) return;

    setActionLoading(true);
    try {
      // Mutation hook auto-invalidates queryKeys.projectAgents on success;
      // no manual loadAgents/loadData needed.
      await deleteAgentMutation.mutateAsync({ agentId: agentToDelete.agent_id });
      setShowDeleteDialog(false);
      setAgentToDelete(null);
    } catch (err) {
      const axiosError = err as AxiosError<{ detail?: { message?: string; workflows?: { id: number; name: string }[]; agents?: { id: number; name: string }[] } }>;
      const detail = axiosError.response?.data?.detail;
      if (axiosError.response?.status === 409 && detail?.workflows) {
        setShowDeleteDialog(false);
        setDeleteBlockedWorkflows(detail.workflows);
      } else if (axiosError.response?.status === 409 && detail?.message) {
        // AC-1730: e.g. the agent is used as a tool by other agents. Surface the
        // actionable backend message instead of a generic failure.
        setShowDeleteDialog(false);
        setError(detail.message);
      } else {
        setError('Failed to delete agent');
      }
      console.error('Error deleting agent:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleFormSubmit = async (formData: CreateAgentForm) => {
    if (!currentProject) return;

    setActionLoading(true);
    try {
      if (editingAgent) {
        // updateAgent and createAgent mutation hooks already invalidate the
        // projectAgents cache key on success - no manual reload needed.
        await updateAgent.mutateAsync({
          agentId: editingAgent.agent_id,
          data: formData,
        });
      } else {
        await createAgent.mutateAsync({ data: formData });
      }
      setShowFormModal(false);
      setEditingAgent(null);
      // Tutorial step 7 is advanced by TutorialOverlay via click trigger on btn-submit-agent
    } catch (err) {
      setError(editingAgent ? 'Failed to update agent' : 'Failed to create agent');
      console.error('Error saving agent:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleSort = (field: SortConfig['field']) => {
    setSortConfig(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getOutputTypeColor = (type: OutputType) => {
    const colors = {
      str: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200',
      model: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300'
    };
    return colors[type] || 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
  };

  if (!currentProject) {
    return (
      <div className="p-4 sm:p-6 max-w-7xl mx-auto">
        <div className="text-center py-12">
          <Bot className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-warm-white mb-2">
            No project selected
          </h3>
          <p className="text-gray-500 dark:text-gray-400">
            Please select a project to manage agents
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      {/* Back navigation */}
      <div className="mb-4">
        {backToWorkflow ? (
          <Link
            to={backToWorkflow.path}
            className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            {backToWorkflow.label}
          </Link>
        ) : currentProject && (
          <Link
            to={`/projects/${currentProject.project_id}`}
            className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to Overview
          </Link>
        )}
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 sm:mb-8">
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-warm-white">Agents</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1 sm:mt-2 text-sm sm:text-base">
            Create and manage your AI agents for {currentProject.name}
          </p>
        </div>
        <div className="relative create-dropdown-container">
          <button
            onClick={() => setShowCreateDropdown(!showCreateDropdown)}
            className="flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
            disabled={loading}
            data-tutorial="btn-create-agent"
          >
            <Plus className="w-4 h-4" />
            New Agent
            <ChevronDown className="w-4 h-4" />
          </button>

          {/* Dropdown Menu - z-[55] to appear above tutorial spotlight (z-50) */}
          {showCreateDropdown && (
            <div className="absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white dark:bg-cap-dark ring-1 ring-black ring-opacity-5 z-[55]">
              <div className="py-1" role="menu">
                <button
                  onClick={handleCreateAgent}
                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:text-warm-white"
                  role="menuitem"
                  data-tutorial="btn-create-from-scratch"
                >
                  <Plus className="w-4 h-4 mr-3" />
                  Create from Scratch
                </button>
                <button
                  onClick={handleCreateFromTemplate}
                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:text-warm-white"
                  role="menuitem"
                >
                  <Sparkles className="w-4 h-4 mr-3" />
                  Use Template
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center">
          <AlertTriangle className="w-5 h-5 text-red-500 mr-2" />
          <span className="text-red-700 dark:text-red-400">{error}</span>
          <button 
            onClick={() => setError(null)}
            className="ml-auto text-red-500 hover:text-red-700"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Search and Filters */}
      <div className="mb-6 space-y-4">
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:space-x-4">
          <div className="flex-1 relative">
            <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search agents..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-warm-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center justify-center px-4 py-2 border rounded-lg transition-colors ${
              showFilters ? 'bg-primary-50 border-blue-300 text-blue-700' : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900'
            }`}
          >
            <Filter className="w-4 h-4 mr-2" />
            Filters
            {showFilters ? <ChevronDown className="w-4 h-4 ml-2" /> : <ChevronRight className="w-4 h-4 ml-2" />}
          </button>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="p-4 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Output Type</label>
                <CustomSelect
                  value={filters.outputType}
                  onChange={(value) => setFilters(prev => ({ ...prev, outputType: value as OutputType | 'all' }))}
                  options={[
                    { value: 'all', label: 'All Output Types' },
                    { value: 'str', label: 'Text' },
                    { value: 'model', label: 'Model' },
                  ]}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Has Models</label>
                <CustomSelect
                  value={filters.hasModels.toString()}
                  onChange={(value) => setFilters(prev => ({
                    ...prev,
                    hasModels: value === 'all' ? 'all' : value === 'true'
                  }))}
                  options={[
                    { value: 'all', label: 'All' },
                    { value: 'true', label: 'With Models' },
                    { value: 'false', label: 'Without Models' },
                  ]}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
          <span className="ml-2 text-gray-600 dark:text-gray-400">Loading agents...</span>
        </div>
      )}

      {/* Agents List */}
      {!loading && (
        <>
          {filteredAndSortedAgents.length === 0 ? (
            <div className="text-center py-12">
              <Bot className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-warm-white mb-2">
                {agents.length === 0 ? 'No agents yet' : 'No agents match your filters'}
              </h3>
              <p className="text-gray-500 dark:text-gray-400 mb-6">
                {agents.length === 0 
                  ? 'Create your first agent to start building workflows'
                  : 'Try adjusting your search or filter criteria'
                }
              </p>
              {agents.length === 0 && (
                <div className="relative create-dropdown-container inline-block">
                  <button
                    onClick={() => setShowEmptyStateDropdown(!showEmptyStateDropdown)}
                    className="flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-lg hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={loading}
                    data-tutorial="btn-create-agent"
                  >
                    <Plus className="w-4 h-4" />
                    Create Agent
                    <ChevronDown className="w-4 h-4" />
                  </button>

                  {/* Dropdown Menu - z-[55] to appear above tutorial spotlight (z-50) */}
                  {showEmptyStateDropdown && (
                    <div className="absolute left-1/2 transform -translate-x-1/2 mt-2 w-56 rounded-md shadow-lg bg-white dark:bg-cap-dark ring-1 ring-black ring-opacity-5 z-[55]">
                      <div className="py-1" role="menu">
                        <button
                          onClick={handleCreateAgent}
                          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:text-warm-white"
                          role="menuitem"
                          data-tutorial="btn-create-from-scratch"
                        >
                          <Plus className="w-4 h-4 mr-3" />
                          Create from Scratch
                        </button>
                        <button
                          onClick={handleCreateFromTemplate}
                          className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:text-warm-white"
                          role="menuitem"
                        >
                          <Sparkles className="w-4 h-4 mr-3" />
                          Use Template
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <AgentTable
              agents={filteredAndSortedAgents}
              models={models}
              sortConfig={sortConfig}
              agentWorkflows={agentWorkflows}
              onSort={handleSort}
              onView={handleViewAgent}
              onEdit={handleEditAgent}
              onDelete={handleDeleteAgent}
            />
          )}
        </>
      )}

      {/* Modals */}
      {showFormModal && (
        <AgentFormModal
          agent={editingAgent}
          models={models}
          availableAgents={agents}
          templateData={templateFormData}
          onSubmit={handleFormSubmit}
          onClose={() => {
            setShowFormModal(false);
            setEditingAgent(null);
            setTemplateFormData(null);
          }}
        />
      )}

      {/* Template Selector */}
      {showTemplateSelector && (
        <AgentTemplateSelector
          projectId={currentProject?.project_id}
          onSelectTemplate={handleTemplateSelect}
          onClose={() => setShowTemplateSelector(false)}
        />
      )}

      {showDetailsModal && selectedAgent && (
        <AgentDetailsModal
          agent={selectedAgent}
          models={models}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedAgent(null);
          }}
          onEdit={() => {
            setShowDetailsModal(false);
            handleEditAgent(selectedAgent);
          }}
        />
      )}

      {showDeleteDialog && agentToDelete && (
        <ConfirmationDialog
          title="Delete Agent"
          message={`Are you sure you want to delete "${agentToDelete.name}"? This action cannot be undone.`}
          confirmText="Delete"
          confirmVariant="danger"
          onConfirm={confirmDeleteAgent}
          onCancel={() => {
            setShowDeleteDialog(false);
            setAgentToDelete(null);
          }}
        />
      )}

      {/* Delete blocked dialog */}
      {deleteBlockedWorkflows && agentToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-cap-dark rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-start space-x-3 mb-4">
              <AlertTriangle className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-warm-white">Cannot Delete Agent</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  "{agentToDelete.name}" is used in the following workflows. Remove the agent from these workflows first.
                </p>
              </div>
            </div>
            <ul className="ml-9 mb-4 space-y-1">
              {deleteBlockedWorkflows.map((w) => (
                <li key={w.id} className="text-sm text-gray-700 dark:text-gray-300 flex items-center space-x-2">
                  <span className="w-1.5 h-1.5 bg-primary-500 rounded-full flex-shrink-0" />
                  <span>{w.name}</span>
                </li>
              ))}
            </ul>
            <div className="flex justify-end">
              <button
                onClick={() => {
                  setDeleteBlockedWorkflows(null);
                  setAgentToDelete(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentsPage;